import FaroSphereLogo from "@assets/icons/faro-sphere-xg-logo-color.svg?react";
import { SphereIconButton } from "@components/common/sphere-icon-button";
import { SphereBreadcrumbs } from "@components/header/sphere-breadcrumbs";
import { MainPageLayoutProps } from "@components/page-layout/main-page-layout";
import { HamburgerMenuIcon, neutral } from "@flat-ui";
import { Box } from "@mui/material";
import { useEffect, useMemo } from "react";
import { NavLink } from "react-router-dom";
import Grid2 from "@mui/material/Grid2";
import { generateProjectsRoute, useAppParams } from "@router/router-helper";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { sidebarSelector } from "@store/ui/ui-selector";
import { updateSidebar } from "@store/ui/ui-slice";
import { DEFAULT_TITLE_FONT_SIZE } from "@styles/common-styles";
import { SidebarEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useMediaQueryList } from "@hooks/use-media-query";

type Props = Pick<
  MainPageLayoutProps,
  | "shouldShowLogo"
  | "shouldHideOpenButton"
  | "shouldShowSidebar"
  | "topLeftHeaderButton"
  | "shouldShowBreadcrumbs"
>;

/** Content for the left side of layout header */
export function HeaderLayoutLeft({
  shouldShowLogo,
  shouldHideOpenButton,
  topLeftHeaderButton,
  shouldShowSidebar,
  shouldShowBreadcrumbs = true,
}: Props): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const dispatch = useAppDispatch();
  const sidebar = useAppSelector(sidebarSelector);
  const { isScreenSmOrLarger } = useMediaQueryList();
  const { companyId } = useAppParams();

  const to = companyId ? generateProjectsRoute(companyId) : "/";

  const { logoHeight, logoML } = useMemo(() => {
    return isScreenSmOrLarger
      ? {
          logoHeight: "3.25rem",
          logoML: "-8px",
        }
      : {
          logoHeight: "3rem",
          logoML: "-16px",
        };
  }, [isScreenSmOrLarger]);

  useEffect(() => {
    dispatch(updateSidebar({ ...sidebar, isVisible: shouldShowSidebar }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Putting sidebar on dependency will create a loop
  }, [dispatch, shouldShowSidebar]);

  return (
    <Grid2 container alignItems="center" flexGrow={1} flexBasis={0}>
      {/* Large button to show on the left side of header */}
      {topLeftHeaderButton}

      {shouldShowLogo && (
        <NavLink style={{ display: "contents" }} to={to}>
          <FaroSphereLogo
            style={{
              width: "138px",
              minWidth: "138px",
              height: logoHeight,
              minHeight: logoHeight,
              marginLeft: logoML,
              marginRight: "10px",
            }}
            onClick={() => {
              trackEvent({ name: SidebarEvents.goHome });
            }}
          />
        </NavLink>
      )}

      {/* Button to open drawer */}
      {!isScreenSmOrLarger && !shouldHideOpenButton && shouldShowSidebar && (
        <Grid2 alignItems="center">
          <Box
            component="div"
            sx={{
              color: neutral[800],
              fontWeight: 600,
              fontSize: DEFAULT_TITLE_FONT_SIZE,
              lineHeight: "28px",
              letterSpacing: "0.16px",
            }}
          >
            <SphereIconButton
              aria-label="open drawer"
              iconButtonProps={{
                edge: "start",
                sx: {
                  mr: 2,
                  display: { sm: "none" },
                },
              }}
              component={HamburgerMenuIcon}
              iconSize="20px"
              onClick={() => {
                dispatch(updateSidebar({ isOpenOnMobile: true }));
              }}
            />
          </Box>
        </Grid2>
      )}

      {shouldShowBreadcrumbs && (
        <Grid2 alignItems="center">
          <SphereBreadcrumbs />
        </Grid2>
      )}
    </Grid2>
  );
}
